import { useEffect, useState } from "react";
import { cn } from "@/lib/utils";
import {
  msUntil,
  onServerTimeSync,
  startServerTimeSync,
} from "@/lib/server-time";

const pad = (n: number) => String(n).padStart(2, "0");

/** Format remaining ms as `02d 14h 36m 21s` (leading units dropped when zero). */
export function formatRemaining(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const d = Math.floor(s / 86400);
  const h = Math.floor((s % 86400) / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  if (d > 0) return `${pad(d)}d ${pad(h)}h ${pad(m)}m ${pad(sec)}s`;
  if (h > 0) return `${pad(h)}h ${pad(m)}m ${pad(sec)}s`;
  if (m > 0) return `${pad(m)}m ${pad(sec)}s`;
  return `${pad(sec)}s`;
}

export type CountdownUrgency = "normal" | "warn" | "urgent" | "critical" | "ended";

export function countdownUrgency(ms: number): CountdownUrgency {
  if (ms <= 0) return "ended";
  if (ms < 60_000) return "critical";
  if (ms < 10 * 60_000) return "urgent";
  if (ms < 60 * 60_000) return "warn";
  return "normal";
}

/**
 * Live countdown driven by the server-synchronised clock.
 *
 * Ticks are scheduled against absolute remaining time (not accumulated
 * intervals), so a tab left open for hours cannot drift, and the offset is
 * re-synced with the database periodically.
 */
export function Countdown({
  endTime,
  className,
  label,
  endedText = "Auction Ended",
  onEnded,
}: {
  endTime: string;
  className?: string;
  /** Optional heading, e.g. "Auction Ends In". */
  label?: string;
  endedText?: string;
  onEnded?: () => void;
}) {
  const [ms, setMs] = useState(() => msUntil(endTime));

  useEffect(() => {
    startServerTimeSync();
    let timer: number | undefined;
    let ended = false;
    const tick = () => {
      const remaining = msUntil(endTime);
      setMs(remaining);
      if (remaining <= 0) {
        if (!ended) {
          ended = true;
          onEnded?.();
        }
        return;
      }
      // Align the next tick to the next whole second of remaining time.
      const delay = remaining % 1000 || 1000;
      timer = window.setTimeout(tick, delay);
    };
    tick();
    const off = onServerTimeSync(() => setMs(msUntil(endTime)));
    return () => {
      if (timer) window.clearTimeout(timer);
      off();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [endTime]);

  const state = countdownUrgency(ms);

  const body = (
    <span
      className={cn(
        "tabular-nums font-medium transition-colors duration-200",
        state === "ended" && "text-muted-foreground",
        state === "critical" && "text-destructive animate-urgent-pulse",
        state === "urgent" && "text-destructive",
        state === "warn" && "text-amber-400",
        state === "normal" && "text-gold",
        className,
      )}
      aria-live={state === "critical" ? "polite" : "off"}
      // Remaining time differs by a second or two between the server render
      // and hydration; the effect above corrects it on the first tick.
      suppressHydrationWarning
    >
      {state === "ended" ? endedText : formatRemaining(ms)}
    </span>
  );

  if (!label) return body;
  return (
    <span className="inline-flex flex-col gap-0.5">
      <span className="text-[10px] uppercase tracking-widest text-muted-foreground">{label}</span>
      {body}
    </span>
  );
}
